import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, Hotel, Calendar, Users, MapPin, BedDouble } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';

export default function CheckInPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hotelId = searchParams.get('hotel');
  
  const [email, setEmail] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [checkedIn, setCheckedIn] = useState(false);
  const [hotel, setHotel] = useState(null);

  useEffect(() => {
    if (hotelId) {
      loadHotel();
    }
  }, [hotelId]);

  const loadHotel = async () => {
    try {
      const backendUrl = BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/hotels/${hotelId}/public`);
      const data = await response.json();
      setHotel(data.hotel);
    } catch (error) {
      console.error('Error loading hotel:', error);
    }
  };

  const handleLookup = async () => {
    if (!email && !confirmationCode) {
      toast.error('Please enter your email or confirmation code');
      return;
    }

    setLoading(true);
    try {
      const backendUrl = BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/checkin/existing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email || undefined,
          confirmation_code: confirmationCode || undefined
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        toast.error(data.detail || 'Booking not found');
        return;
      }

      if (data.multiple_bookings) {
        setBookings(data.bookings);
        toast.success(`Found ${data.bookings.length} booking(s)`);
      } else if (data.already_checked_in) {
        toast.info('Already checked in!');
        setSelectedBooking(data.booking);
        setCheckedIn(true);
      } else {
        setSelectedBooking(data.booking);
        setCheckedIn(true);
        toast.success(data.message);
      }
    } catch (error) {
      console.error('Check-in error:', error);
      toast.error('Failed to find booking');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async (bookingId) => {
    setLoading(true);
    try {
      const backendUrl = BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/checkin/existing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_id: bookingId })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSelectedBooking(data.booking);
        setCheckedIn(true);
        setBookings([]);
        toast.success(data.message);
      }
    } catch (error) {
      console.error('Check-in error:', error);
      toast.error('Failed to check in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="checkin-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Hotel className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold heading-font mb-2">
            {hotel ? `Welcome to ${hotel.name}` : 'Self Check-In'}
          </h1>
          <p className="text-muted-foreground">
            {hotel?.city && `${hotel.city}, ${hotel.country}`}
          </p>
        </div>

        {!checkedIn && bookings.length === 0 && (
          <Card data-testid="checkin-lookup-card">
            <CardHeader>
              <CardTitle className="heading-font">Find Your Booking</CardTitle>
              <CardDescription>Enter your email or confirmation code to check in</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Email Address</Label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="checkin-email-input"
                />
              </div>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Or</span>
                </div>
              </div>

              <div>
                <Label>Confirmation Code</Label>
                <Input
                  placeholder="ABC123XYZ456"
                  value={confirmationCode}
                  onChange={(e) => setConfirmationCode(e.target.value.toUpperCase())}
                  data-testid="checkin-code-input"
                />
              </div>

              <Button
                onClick={handleLookup}
                disabled={loading || (!email && !confirmationCode)}
                className="w-full h-12"
                data-testid="checkin-lookup-button"
              >
                {loading ? 'Searching...' : 'Find My Booking'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Multiple Bookings Found */}
        {bookings.length > 0 && (
          <div className="space-y-4" data-testid="checkin-bookings-list">
            <h2 className="text-xl font-semibold heading-font">Select Your Booking</h2>
            {bookings.map((booking) => (
              <Card key={booking.id} className="hover:border-primary transition-colors">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="font-semibold">{booking.guest_name}</p>
                      <p className="text-sm text-muted-foreground mono">#{booking.confirmation_code}</p>
                    </div>
                    {booking.checked_in ? (
                      <Badge variant="outline" className="bg-green-50 text-green-700">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Checked In
                      </Badge>
                    ) : (
                      <Badge variant="secondary">Ready to Check In</Badge>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      {booking.check_in}
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Users className="w-4 h-4" />
                      {booking.guests} guest{booking.guests > 1 ? 's' : ''}
                    </div>
                  </div>

                  {!booking.checked_in && (
                    <Button
                      onClick={() => handleCheckIn(booking.id)}
                      disabled={loading}
                      className="w-full mt-4"
                      data-testid="checkin-button"
                    >
                      Check In Now
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Check-in Success */}
        {checkedIn && selectedBooking && (
          <Card className="border-green-200" data-testid="checkin-success-card">
            <CardHeader>
              <div className="flex items-center justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
              </div>
              <CardTitle className="text-center heading-font text-2xl">Welcome!</CardTitle>
              <CardDescription className="text-center">You're checked in, {selectedBooking.guest_name}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <AlertDescription className="text-green-900">
                  Check-in completed at {new Date(selectedBooking.checked_in_at || Date.now()).toLocaleTimeString()}
                </AlertDescription>
              </Alert>

              <div className="bg-muted rounded-xl p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <BedDouble className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Your Room</p>
                    <p className="font-semibold">Check with front desk</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Stay Duration</p>
                    <p className="font-semibold">{selectedBooking.check_in} → {selectedBooking.check_out}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Guests</p>
                    <p className="font-semibold">{selectedBooking.guests} guest{selectedBooking.guests > 1 ? 's' : ''}</p>
                  </div>
                </div>
              </div>

              <p className="text-sm text-muted-foreground text-center">
                Please proceed to the front desk to receive your room key. Enjoy your stay!
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
